import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { apiService } from '@/lib/api';
import { LoadingSpinner } from './ui/LoadingSpinner';
import type { BillingChargePreview } from '@/types';

function usd(cents: number): string {
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function apiErr(e: unknown, fallback: string): string {
    const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
    return msg ?? (e instanceof Error ? e.message : fallback);
}

interface ChargeConfirmModalProps {
    open: boolean;
    planKey: string | null;
    billingPeriod: 'Monthly' | 'Annual';
    action: 'subscribe' | 'change_plan';
    onClose: () => void;
    /** Receives the preview it just confirmed, so the caller can report the exact amount charged. */
    onConfirm: (preview: BillingChargePreview) => Promise<void>;
}

/**
 * Confirms the amount that will be charged now before subscribe / re-subscribe / upgrade.
 * Portaled above the lock screen (z-9500) so it is usable there too.
 */
export function ChargeConfirmModal({
    open, planKey, billingPeriod, action, onClose, onConfirm,
}: ChargeConfirmModalProps) {
    const layer = useModalLayer({ enabled: open, onClose, backNavigation: false });
    const [preview, setPreview] = useState<BillingChargePreview | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [confirmError, setConfirmError] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !planKey) {
            setPreview(null);
            setError(null);
            setConfirming(false);
            setConfirmError(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        setPreview(null);
        apiService.previewCharge(planKey, billingPeriod, action)
            .then((p) => { if (!cancelled) setPreview(p); })
            .catch((e) => { if (!cancelled) setError(apiErr(e, 'Could not load the charge amount.')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, planKey, billingPeriod, action]);

    if (!open) return null;

    const handleConfirm = async () => {
        if (confirming || !preview) return;
        setConfirming(true);
        setConfirmError(null);
        try {
            await onConfirm(preview);
            onClose();
        } catch (e) {
            setConfirmError(apiErr(e, 'Could not complete the charge.'));
        } finally {
            setConfirming(false);
        }
    };

    const copy = !preview ? null
        : preview.scheduled ? 'No charge now. This plan change takes effect at your next billing date.'
        : preview.needs_checkout ? `You'll enter your card next. Then we'll charge ${usd(preview.total_cents)}.`
        : preview.total_cents === 0 ? "You won't be charged now."
        : `You'll be charged ${usd(preview.total_cents)} now.`;

    const overlay = (
        <LayerDepthContext.Provider value={layer.depth}>
            <div
                className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4"
                // Lock screen sits at z-9500; layer z-index alone would paint under it.
                style={{ zIndex: Math.max(layer.zIndex, 9600) }}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="charge-confirm-title"
            >
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-y-auto">
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <h2 id="charge-confirm-title" className="text-xl font-semibold text-gray-900">
                            Confirm charge
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={confirming}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                            <X className="h-5 w-5 text-gray-500" />
                        </button>
                    </div>

                    <div className="p-6">
                        {loading && <div className="text-center py-6"><LoadingSpinner /></div>}
                        {error && (
                            <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                                {error}
                            </div>
                        )}
                        {confirmError && (
                            <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-3">
                                {confirmError}
                            </div>
                        )}
                        {preview && (
                            <>
                                <p className="text-gray-700">{copy}</p>
                                {preview.lines.length > 0 && (
                                    <ul className="mt-4 space-y-1 text-sm text-gray-600">
                                        {preview.lines.map((l, i) => (
                                            <li key={i} className="flex justify-between gap-4">
                                                <span>{l.description}</span>
                                                <span className="tabular-nums">{usd(l.amount_cents)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between font-medium text-gray-900">
                                    <span>Total due now</span>
                                    <span className="tabular-nums">{usd(preview.total_cents)}</span>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={confirming}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={confirming || loading || !preview || !!error}
                            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            {confirming ? 'Working…' : preview?.needs_checkout ? 'Continue to payment' : 'Confirm'}
                        </button>
                    </div>
                </div>
            </div>
        </LayerDepthContext.Provider>
    );

    return createPortal(overlay, document.body);
}
